/**
 * Render an EPC GiroCode payload as a PNG data URL (error correction M).
 */
import QRCode from 'qrcode';
import type { CompanySettings } from '../types';
import {
  buildEpcQrPayload,
  documentTypeSupportsPaymentQr,
  type BuildEpcQrPayloadInput,
} from './epcQrPayload';

export type ResolvePaymentQrInput = BuildEpcQrPayloadInput & {
  documentTypeLabel?: string | null;
};

export function paymentQrBeneficiaryName(company: CompanySettings | null | undefined): string {
  const holder = (company?.bank_account_holder ?? '').trim();
  if (holder) return holder;
  return (company?.company_name ?? '').trim();
}

/** Build resolve input from company + document; null when clearly not applicable. */
export function paymentQrResolveInput(opts: {
  company: CompanySettings | null | undefined;
  documentTypeLabel?: string | null;
  grossTotal: number;
  remittanceText?: string | null;
  /** When set, also require body.paymentQr === true. */
  layout?: { body?: { paymentQr?: boolean } } | null;
}): ResolvePaymentQrInput | null {
  if (opts.layout !== undefined && opts.layout?.body?.paymentQr !== true) return null;
  if (!documentTypeSupportsPaymentQr(opts.documentTypeLabel)) return null;
  const company = opts.company;
  if (!company) return null;
  const amount = Number(opts.grossTotal);
  if (!Number.isFinite(amount) || amount < 0.01) return null;
  const name = paymentQrBeneficiaryName(company);
  const iban = (company.iban ?? '').trim();
  if (!name || !iban) return null;
  return {
    documentTypeLabel: opts.documentTypeLabel,
    name,
    iban,
    amountEur: amount,
    bic: company.bic,
    remittanceText: opts.remittanceText,
  };
}

/** Returns a PNG data URL, or null when the document/type/data should not show a QR. */
export async function resolvePaymentQrDataUrl(
  input: ResolvePaymentQrInput | null,
): Promise<string | null> {
  if (!input) return null;
  if (!documentTypeSupportsPaymentQr(input.documentTypeLabel)) return null;
  const built = buildEpcQrPayload(input);
  if (!built.ok) return null;
  try {
    return await QRCode.toDataURL(built.payload, {
      errorCorrectionLevel: 'M',
      margin: 1,
      width: 256,
      type: 'image/png',
    });
  } catch {
    return null;
  }
}

/** Sync-friendly check used by guards. */
export function canBuildPaymentQrPayload(input: ResolvePaymentQrInput | null): boolean {
  if (!input) return false;
  if (!documentTypeSupportsPaymentQr(input.documentTypeLabel)) return false;
  return buildEpcQrPayload(input).ok;
}

/** ~2×2 cm EPC-recommended minimum; slightly larger for comfortable scanning. */
export const PAYMENT_QR_SIZE_PT = 64;

/**
 * Short caption under the GiroCode on invoices.
 * Common practice (Lexware/sevDesk often omit text; generators recommend a one-liner):
 * explain scan-with-banking-app without sounding like a payment link.
 */
export const PAYMENT_QR_CAPTION = 'Mit Banking-App\nscannen und bezahlen';
